import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { ArrowLeft, Clock, Loader2, Save } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function buildTimeOptions() {
    const opts = [];
    for (let h = 6; h <= 21; h++) {
        for (const m of ["00", "30"]) {
            opts.push(`${String(h).padStart(2, "0")}:${m}`);
        }
    }
    return opts;
}
const TIME_OPTIONS = buildTimeOptions();

function formatTime12(t) {
    const [hh, mm] = t.split(":").map(Number);
    const period = hh >= 12 ? "PM" : "AM";
    const h12 = hh % 12 === 0 ? 12 : hh % 12;
    return `${h12}:${String(mm).padStart(2, "0")} ${period}`;
}

const GROUP_LABELS = {
    weekday: "Lunes a Viernes",
    saturday: "Sábado",
    sunday: "Domingo",
};

const TimeSelect = ({ label, value, onChange, testId }) => (
    <div>
        <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium block mb-1">
            {label}
        </label>
        <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="h-10 rounded-xl border border-input bg-background px-2 text-sm"
            data-testid={testId}
        >
            {TIME_OPTIONS.map((t) => (
                <option key={t} value={t}>{formatTime12(t)}</option>
            ))}
        </select>
    </div>
);

export default function AdminSchedule({ onBack }) {
    const [schedule, setSchedule] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        axios
            .get(`${API}/admin/schedule`, { withCredentials: true })
            .then((res) => setSchedule(res.data))
            .catch(() => toast.error("No se pudo cargar el horario"));
    }, []);

    const setGroup = (g, field, value) =>
        setSchedule((s) => ({ ...s, [g]: { ...s[g], [field]: value } }));

    const save = async () => {
        for (const g of ["weekday", "saturday", "sunday"]) {
            if (schedule[g].enabled && schedule[g].start > schedule[g].last) {
                toast.error(`${GROUP_LABELS[g]}: la primera cita debe ser antes de la última`);
                return;
            }
        }
        setSaving(true);
        try {
            await axios.put(`${API}/admin/schedule`, schedule, { withCredentials: true });
            toast.success("Horario actualizado. Los cambios aplican de inmediato ⏰");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "No se pudo guardar el horario");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="min-h-screen bg-background">
            <main className="max-w-3xl mx-auto px-5 sm:px-8 py-10">
                <Button
                    variant="outline"
                    onClick={onBack}
                    className="rounded-2xl mb-6"
                    data-testid="schedule-back-btn"
                >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Volver a citas
                </Button>

                <div className="mb-8">
                    <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-2 inline-flex items-center gap-2">
                        <Clock className="h-3.5 w-3.5 text-primary" />
                        Horario de atención
                    </p>
                    <h1 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight">
                        Días y horarios disponibles
                    </h1>
                    <p className="text-muted-foreground mt-2">
                        Enciende o apaga días y define la primera y última cita. Los cambios
                        aplican de inmediato en la página de reservas y en la disponibilidad.
                    </p>
                </div>

                {!schedule ? (
                    <div className="flex items-center justify-center py-16 text-muted-foreground">
                        <Loader2 className="h-5 w-5 animate-spin mr-2" />
                        Cargando...
                    </div>
                ) : (
                    <div className="space-y-4">
                        {["weekday", "saturday", "sunday"].map((g) => (
                            <Card
                                key={g}
                                className={`rounded-2xl border p-5 transition-colors ${
                                    schedule[g].enabled ? "border-primary/40 bg-primary/5" : "border-border bg-card opacity-80"
                                }`}
                                data-testid={`schedule-group-${g}`}
                            >
                                <div className="flex items-center justify-between gap-3 mb-4">
                                    <p className="font-heading text-base font-medium">{GROUP_LABELS[g]}</p>
                                    <div className="flex items-center gap-2">
                                        <span
                                            className={`text-xs font-semibold uppercase tracking-wider ${
                                                schedule[g].enabled ? "text-primary" : "text-muted-foreground"
                                            }`}
                                        >
                                            {schedule[g].enabled ? "Abierto" : "Cerrado"}
                                        </span>
                                        <Switch
                                            checked={schedule[g].enabled}
                                            onCheckedChange={(v) => setGroup(g, "enabled", v)}
                                            data-testid={`schedule-switch-${g}`}
                                        />
                                    </div>
                                </div>
                                {schedule[g].enabled && (
                                    <div className="flex flex-wrap items-end gap-4">
                                        <TimeSelect
                                            label="Primera cita"
                                            value={schedule[g].start}
                                            onChange={(v) => setGroup(g, "start", v)}
                                            testId={`schedule-start-${g}`}
                                        />
                                        <TimeSelect
                                            label="Última cita"
                                            value={schedule[g].last}
                                            onChange={(v) => setGroup(g, "last", v)}
                                            testId={`schedule-last-${g}`}
                                        />
                                        <p className="text-xs text-muted-foreground pb-2.5">
                                            Citas cada 30 min de {formatTime12(schedule[g].start)} a{" "}
                                            {formatTime12(schedule[g].last)}
                                        </p>
                                    </div>
                                )}
                            </Card>
                        ))}
                        <Button
                            onClick={save}
                            disabled={saving}
                            className="w-full h-12 rounded-2xl text-base"
                            data-testid="schedule-save-btn"
                        >
                            {saving ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                                <Save className="h-4 w-4 mr-2" />
                            )}
                            Guardar horario
                        </Button>
                    </div>
                )}
            </main>
        </div>
    );
}
